import { cn } from '@resolute/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useRef } from 'react';
import { Link } from 'react-router-dom';


import { type SimpleNavDropdown } from './nav-menu';

import { PANEL_TRANSITION } from '@/lib/motion';


/**
 * A small top-level dropdown for a short, flat list of links (e.g. About Us /
 * Leadership) — a plain popover, not the two-panel mega-menu `NavDropdown`
 * above. Shares the same hover-intent/keyboard-focus/outside-click behavior
 * (via the same open/close handlers passed down from `Header`) so it feels
 * consistent with the other nav items, just visually lighter.
 */
export function NavSimpleDropdown({
  dropdown,
  isOpen,
  onOpen,
  onHoverOpen,
  onToggle,
  onCloseRequest,
  onScheduleClose,
  onMeasure,
}: {
  dropdown: SimpleNavDropdown;
  isOpen: boolean;
  onOpen: () => void;
  onHoverOpen: () => void;
  onToggle: () => void;
  onCloseRequest: () => void;
  onScheduleClose: () => void;
  onMeasure: (el: HTMLElement | null) => void;
}) {
  const reduce = useReducedMotion();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pointerFocusRef = useRef(false);

  return (
    <li
      className="relative"
      onMouseEnter={() => {
        onHoverOpen();
        onMeasure(triggerRef.current);
      }}
      onMouseLeave={onScheduleClose}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={`menu-${dropdown.id}`}
        onClick={onToggle}
        onPointerDown={() => {
          pointerFocusRef.current = true;
        }}
        onFocus={(e) => {
          onMeasure(e.currentTarget);
          if (pointerFocusRef.current) {
            pointerFocusRef.current = false;
            return;
          }
          onOpen();
        }}
        className={cn(
          'inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-fast ease-out-expo hover:bg-muted',
          isOpen ? 'text-primary' : 'text-foreground',
        )}
      >
        {dropdown.label}
        <ChevronDown
          className={cn('size-4 transition-transform duration-fast', isOpen && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={`menu-${dropdown.id}`}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
            transition={PANEL_TRANSITION}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) onCloseRequest();
            }}
            className={cn(
              'border-border bg-popover absolute left-0 top-full mt-2 origin-top-left overflow-hidden rounded-xl border p-2 shadow-xl',
              dropdown.groups && dropdown.groups.length > 1 ? 'min-w-[24rem]' : 'min-w-[12rem]',
            )}
          >
            {dropdown.groups ? (
              <div className="flex flex-col gap-4 p-1 sm:flex-row sm:gap-6">
                {dropdown.groups.map((group, idx) => (
                  <div key={idx} className="flex min-w-[10.5rem] flex-1 flex-col gap-1">
                    {group.heading && (
                      <p className="text-muted-foreground/80 border-border/40 mb-1 border-b pb-1.5 pl-3 text-[11px] font-bold uppercase tracking-wider">
                        {group.heading}
                      </p>
                    )}
                    <ul className="flex flex-col gap-0.5">
                      {group.items.map((item) => (
                        <li key={item.to}>
                          <Link
                            to={item.to}
                            className="text-foreground hover:bg-muted hover:text-primary duration-fast ease-out-expo block rounded-md px-3 py-2 text-sm font-medium transition-colors"
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="flex flex-col">
                {(dropdown.items ?? []).map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="text-foreground hover:bg-muted hover:text-primary duration-fast ease-out-expo block rounded-md px-3 py-2 text-sm font-medium transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

